import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { can } from '../../api/profile';
import { getEvents, type Event } from '../../features/events';
import { getProjects, type Project } from '../../features/projects';
import { getNotices, type ImportantNotice } from '../../api/notices';
import { getClasses, type ClassOffering } from '../../api/classes';
import { getGallery } from '../../api/gallery';
import type { Gallery } from '../../features/gallery/types';
import { getReviews } from '../../api/reviews';
import type { Review } from '../../features/reviews/types';
import { getCommittee, type CommitteeMember } from '../../api/committee';
import { getTimeline, type TimelineEntry } from '../../api/timeline';
import {
  Card,
  DashboardPageHeader,
  DashboardStatCard,
  type DashboardStatTone,
} from '../../shared/ui';

type OverviewTile = {
  key: string;
  title: string;
  to: string;
  icon: string;
  tone: DashboardStatTone;
  value: React.ReactNode;
  description: React.ReactNode;
};

const DashboardOverviewPage: React.FC = () => {
  const { user, profile } = useAuth();
  const [events, setEvents] = useState<Event[] | null>(null);
  const [projects, setProjects] = useState<Project[] | null>(null);
  const [notices, setNotices] = useState<ImportantNotice[] | null>(null);
  const [classes, setClasses] = useState<ClassOffering[] | null>(null);
  const [gallery, setGallery] = useState<Gallery[] | null>(null);
  const [reviews, setReviews] = useState<Review[] | null>(null);
  const [committee, setCommittee] = useState<CommitteeMember[] | null>(null);
  const [timeline, setTimeline] = useState<TimelineEntry[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!profile) return;

    const tasks: Array<Promise<unknown>> = [];

    const load = <T,>(
      allowed: boolean,
      fetcher: () => Promise<T>,
      setter: React.Dispatch<React.SetStateAction<T | null>>
    ) => {
      if (!allowed) {
        setter(null);
        return;
      }
      tasks.push(
        fetcher()
          .then(setter)
          .catch((err) => {
            console.error(err);
            setter([] as unknown as T);
          })
      );
    };

    load(can(profile, 'events', 'view'), getEvents, setEvents);
    load(can(profile, 'projects', 'view'), getProjects, setProjects);
    load(can(profile, 'notices', 'view'), getNotices, setNotices);
    load(can(profile, 'classes', 'view'), getClasses, setClasses);
    load(can(profile, 'gallery', 'view'), getGallery, setGallery);
    load(can(profile, 'reviews', 'view'), getReviews, setReviews);
    load(can(profile, 'committee', 'view'), getCommittee, setCommittee);
    load(can(profile, 'timeline', 'view'), getTimeline, setTimeline);

    setLoading(true);
    Promise.all(tasks).finally(() => setLoading(false));
  }, [profile]);

  const tiles = useMemo((): OverviewTile[] => {
    const next: OverviewTile[] = [];

    if (events) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const upcoming = events.filter((e) => {
        const d = new Date(e.dtDateOfEvent);
        d.setHours(0, 0, 0, 0);
        return d.getTime() >= today.getTime();
      }).length;
      const isPublic = events.filter((e) => e.isPublic).length;
      next.push({
        key: 'events',
        title: 'Events',
        to: '/dashboard/events',
        icon: 'bi bi-calendar-event',
        tone: 'primary',
        value: events.length,
        description: (
          <>
            Upcoming: <span className="text-body">{upcoming}</span> · Public:{' '}
            <span className="text-body">{isPublic}</span>
          </>
        ),
      });
    }

    if (projects) {
      const active = projects.filter((p) => !p.isCompleted).length;
      const completed = projects.filter((p) => p.isCompleted).length;
      const isPublic = projects.filter((p) => p.isPublic).length;
      next.push({
        key: 'projects',
        title: 'Projects',
        to: '/dashboard/projects',
        icon: 'bi bi-folder',
        tone: 'success',
        value: projects.length,
        description: (
          <>
            Active: <span className="text-body">{active}</span> · Completed:{' '}
            <span className="text-body">{completed}</span> · Public:{' '}
            <span className="text-body">{isPublic}</span>
          </>
        ),
      });
    }

    if (committee) {
      next.push({
        key: 'committee',
        title: 'Committee',
        to: '/dashboard/committee',
        icon: 'bi bi-people-fill',
        tone: 'info',
        value: committee.length,
        description: 'Committee members',
      });
    }

    if (timeline) {
      const active = timeline.filter((t) => t.isActive).length;
      next.push({
        key: 'timeline',
        title: 'Timeline',
        to: '/dashboard/timeline',
        icon: 'bi bi-clock-history',
        tone: 'neutral',
        value: timeline.length,
        description: (
          <>
            Active: <span className="text-body">{active}</span>
          </>
        ),
      });
    }

    if (notices) {
      next.push({
        key: 'notices',
        title: 'Notices',
        to: '/dashboard/notices',
        icon: 'bi bi-megaphone',
        tone: 'warning',
        value: notices.length,
        description: 'Total notices',
      });
    }

    if (classes) {
      const isPublic = classes.filter((c) => c.isPublic).length;
      next.push({
        key: 'classes',
        title: 'Classes',
        to: '/dashboard/classes',
        icon: 'bi bi-book',
        tone: 'info',
        value: classes.length,
        description: (
          <>
            Public: <span className="text-body">{isPublic}</span>
          </>
        ),
      });
    }

    if (gallery) {
      const active = gallery.filter((g) => g.isActive).length;
      next.push({
        key: 'gallery',
        title: 'Gallery',
        to: '/dashboard/gallery',
        icon: 'bi bi-images',
        tone: 'primary',
        value: gallery.length,
        description: (
          <>
            Active: <span className="text-body">{active}</span>
          </>
        ),
      });
    }

    if (reviews) {
      const active = reviews.filter((r) => r.isActive).length;
      next.push({
        key: 'reviews',
        title: 'Reviews',
        to: '/dashboard/reviews',
        icon: 'bi bi-chat-quote',
        tone: 'primary',
        value: reviews.length,
        description: (
          <>
            Active: <span className="text-body">{active}</span>
          </>
        ),
      });
    }

    return next;
  }, [events, projects, notices, classes, gallery, reviews, committee, timeline]);

  if (!user || !profile) return null;

  return (
    <div className="container-fluid py-4 dashboard-overview-page">
      <DashboardPageHeader
        title="Overview"
        icon="bi bi-speedometer2"
        description={
          <>
            Welcome back, <span className="fw-semibold text-body">{profile.name}</span>. At-a-glance
            status across the areas you can access.
          </>
        }
      />

      <Card title="Status" icon="bi bi-grid-3x3-gap">
        {loading ? (
          <div className="row g-3">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="col-12 col-sm-6 col-lg-4 col-xl-3">
                <div className="dashboard-overview-skeleton" aria-hidden>
                  <div className="dashboard-overview-skeleton__line dashboard-overview-skeleton__line--sm" />
                  <div className="dashboard-overview-skeleton__line dashboard-overview-skeleton__line--lg" />
                  <div className="dashboard-overview-skeleton__line dashboard-overview-skeleton__line--md" />
                </div>
              </div>
            ))}
            <span className="visually-hidden">Loading overview status…</span>
          </div>
        ) : tiles.length > 0 ? (
          <div className="row g-3">
            {tiles.map((tile) => (
              <div key={tile.key} className="col-12 col-sm-6 col-lg-4 col-xl-3">
                <DashboardStatCard
                  title={tile.title}
                  value={tile.value}
                  description={tile.description}
                  icon={tile.icon}
                  tone={tile.tone}
                  to={tile.to}
                />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-4 text-muted">
            <i className="bi bi-shield-exclamation fs-2 d-block mb-2" aria-hidden />
            No overview tiles available for your current permissions.
          </div>
        )}
      </Card>
    </div>
  );
};

export default DashboardOverviewPage;
